import { useEffect, useRef, useState } from 'react';
import { markActivity, getLastActivity, onOtherTabActivity } from './sessionActivity.js';

// Used until /me reports the server's session_timeout_seconds - same 15-minute
// fallback AuthFilter applies when no SessionTimeout is configured.
export const DEFAULT_IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const WARNING_MS = 60 * 1000;

// Logs out once idleTimeoutMs passes without a click or keystroke in any portal tab
// (see sessionActivity.js). Shortly before, secondsLeft becomes a live countdown for the
// "still there?" modal; from then on only its "stay signed in" button (keepAlive) keeps
// the session. 401s are handled by useSession, not here.
export function useAutoLogout(loginId, onLogout, idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS) {
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;
  const [secondsLeft, setSecondsLeft] = useState(null);
  const keepAliveRef = useRef(() => {});

  useEffect(() => {
    if (!loginId) {
      setSecondsLeft(null);
      return;
    }

    // Half the timeout at most, so a very short SessionTimeout still has an idle phase.
    const warningMs = Math.min(WARNING_MS, idleTimeoutMs / 2);
    let logoutTimeoutId;
    let warnTimeoutId;
    let tickIntervalId;
    let warningShown = false;
    // Deliberate actions only - pointerdown covers mouse clicks and taps alike.
    const activityEvents = ['pointerdown', 'keydown'];

    const clearAll = () => {
      clearTimeout(logoutTimeoutId);
      clearTimeout(warnTimeoutId);
      clearInterval(tickIntervalId);
    };

    // Plans the warning and logout from the latest activity across all tabs.
    const schedule = () => {
      clearAll();
      warningShown = false;
      setSecondsLeft(null);

      const logoutAt = getLastActivity() + idleTimeoutMs;

      warnTimeoutId = setTimeout(() => {
        warningShown = true;
        setSecondsLeft(Math.ceil(warningMs / 1000));
        tickIntervalId = setInterval(() => {
          setSecondsLeft(Math.max(0, Math.ceil((logoutAt - Date.now()) / 1000)));
        }, 1000);
      }, Math.max(0, logoutAt - warningMs - Date.now()));

      // Re-checked on fire - another tab may have been active without this one hearing it.
      logoutTimeoutId = setTimeout(() => {
        if (getLastActivity() + idleTimeoutMs > Date.now()) schedule();
        else onLogoutRef.current();
      }, Math.max(0, logoutAt - Date.now()));
    };

    // Ignored while the warning is up - staying signed in takes the explicit button.
    const handleActivity = () => {
      if (!warningShown && markActivity()) schedule();
    };
    keepAliveRef.current = () => { markActivity({ force: true }); schedule(); };

    markActivity({ force: true });
    schedule();
    const unsubscribe = onOtherTabActivity(schedule);
    for (const eventName of activityEvents) {
      window.addEventListener(eventName, handleActivity, { passive: true });
    }

    return () => {
      clearAll();
      unsubscribe();
      for (const eventName of activityEvents) {
        window.removeEventListener(eventName, handleActivity);
      }
    };
  }, [loginId, idleTimeoutMs]);

  return { secondsLeft, keepAlive: () => keepAliveRef.current() };
}
